import { AfterViewInit, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { tap, timeout } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { ApiDataSource } from '../../utils/api-data-source';
import { AppService } from '../../app.service';
import { ApiRequestService } from '../api-request.service';
import { UtilsService } from '../utils.service';

@Component({
  selector: 'app-hazardous-substances-selector-for-ta',
  templateUrl: './hazardous-substances-selector-for-ta.component.html',
  styleUrls: ['./hazardous-substances-selector-for-ta.component.scss']
})
export class HazardousSubstancesSelectorForTaComponent implements OnInit, OnDestroy, AfterViewInit {

  // Columns to show. The non-sortable virtual `linked_to_site` column is reserved for the
  // site auto-suggest badge that is wired in task 7.4.
  displayedColumns: string[] = [
    'select',
    'id',
    'substance',
    'hazardous_substance_type',
    'linked_to_site',
    'date_created'
  ];

  dataSource: HazardousSubstancesSelectorForTaDataSource;

  // Site context passed in via MAT_DIALOG_DATA. Used by the site auto-suggest fetch in 7.3/7.4.
  site_id: number | null = null;

  /**
   * IDs of hazardous substances already linked to `site_id`, populated by the site
   * auto-suggest fetch.
   *
   * Stays empty when `site_id` is missing/falsy, when the request returns a non-2xx
   * response, when it times out (5 seconds), or when it errors at the network level.
   * The fetch fires whenever `site_id` is truthy. RBAC is intentionally NOT gated on
   * this branch: the platform's RBAC framework is not yet wired up here, so the only
   * precondition is having a valid `site_id`.
   *
   * Used by the badge column and the bulk "Select all linked to this site" control
   * added in task 7.4 (rendered only when `siteLinkedIds.size > 0`).
   */
  siteLinkedIds: Set<number> = new Set<number>();

  /**
   * UI state for the account-level catalogue load.
   *
   * - `loading` is true while the most recent request is in flight.
   * - `loadFailed` is true when the most recent request errored. While true, an inline
   *   `mat-error` banner is rendered and confirm is suppressed (the dialog still cancels cleanly).
   * - `hasLoadedOnce` flips true the first time a load completes (success or failure).
   *   The empty-state message is gated on `hasLoadedOnce && !loading && !loadFailed && total === 0`
   *   so it does not flash before the first request resolves.
   * - `total` mirrors the latest record count exposed by the data source so the empty-state
   *   branch can render without using the async pipe inside `*ngIf`.
   */
  loading: boolean = false;
  loadFailed: boolean = false;
  hasLoadedOnce: boolean = false;
  total: number = 0;

  // The paginator and sorter.
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  private totalSubscription: Subscription;
  private siteLinkedSubscription: Subscription;
  private viewSubscriptions: Subscription[] = [];

  constructor(
    public app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService,
    public dialogRef: MatDialogRef<HazardousSubstancesSelectorForTaComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    // Default `multiple` to true (TA links a set of hazardous substances).
    if (typeof this.data['multiple'] === 'undefined') {
      this.data['multiple'] = true;
    }

    // Default `selected` to an empty array.
    if (typeof this.data['selected'] === 'undefined') {
      this.data['selected'] = [];
    }

    // Capture the optional site context for the auto-suggest fetch added later.
    if (typeof this.data['site_id'] !== 'undefined' && this.data['site_id']) {
      this.site_id = this.data['site_id'];
    }

    // Construct the data source. The `multiple` flag is forwarded to the underlying
    // `SelectionModel`, which enforces the single-selection invariant when `multiple === false`
    // (toggling a new row replaces the existing selection rather than adding to it).
    this.dataSource = new HazardousSubstancesSelectorForTaDataSource(
      this.app,
      this.api,
      this.data['multiple']
    );

    // Pre-select rows whose ID is in the `selected` input. When `multiple === false` and the
    // caller pre-seeds more than one ID (defensive), the SelectionModel keeps only the last one.
    for (let i = 0; i < this.data['selected'].length; i++) {
      if (this.data['selected'][i]) {
        this.dataSource.selection.select(this.data['selected'][i]);
      }
    }
  }

  /**
   * Badge mode is derived from the truthiness of the `site_id` input. The two modes are
   * mutually exclusive (R8.9, R8.10).
   *
   * - Site mode (truthy `site_id`): the component fetches the site-linked substances and
   *   renders the "Linked to this site" badge plus the "Select all linked to this site"
   *   bulk control (R9). This is the existing behaviour.
   * - Template mode (no/falsy `site_id`): the component skips the `Site_HS_Source` fetch
   *   entirely and instead renders an informational "Linked to site(s)" badge on any row
   *   whose `sites_count` is greater than zero, with no preselect and no bulk control (R16).
   */
  get isSiteMode(): boolean {
    return !!this.site_id;
  }

  ngOnInit() {
    // Mirror the data source's total observable into a component-level field so the
    // template can reference it inside structural directives without `async` chaining.
    this.totalSubscription = this.dataSource.total.subscribe((total) => {
      this.total = total;
    });

    this.loadAccountList();

    // Site auto-suggest is site mode only. The fetch short-circuits cleanly in template
    // mode because `loadSiteLinkedIds()` returns early when `site_id` is falsy, so
    // `siteLinkedIds` stays empty and no `Site_HS_Source` request is issued (R8.10).
    this.loadSiteLinkedIds();
  }

  ngAfterViewInit() {
    // Reset the paginator when sorting.
    this.viewSubscriptions.push(
      this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0))
    );

    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.order_by = this.sort.active;
      this.dataSource.order = this.sort.direction;

      this.loadAccountList(false);
    });

    // Subscribe to the paginator and sort tap events.
    this.viewSubscriptions.push(
      this.paginator.page.pipe(_tap).subscribe()
    );
    this.viewSubscriptions.push(
      this.sort.sortChange.pipe(_tap).subscribe()
    );
  }

  ngOnDestroy() {
    if ( this.totalSubscription ) {
      this.totalSubscription.unsubscribe();
    }

    if ( this.siteLinkedSubscription ) {
      this.siteLinkedSubscription.unsubscribe();
    }

    this.viewSubscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Fetch the IDs of hazardous substances already linked to `site_id` and populate
   * `siteLinkedIds` for the badge column / bulk control wired in task 7.4.
   *
   * Behaviour (R9.1, R9.5):
   *
   * - Fires only when `site_id` is truthy. The platform's RBAC framework is NOT yet
   *   wired up on this branch, so no permission check is performed; this matches the
   *   intentional scope of this branch (RBAC will be re-introduced in a later spec).
   * - Pipes the response through `timeout(5000)` from `rxjs/operators` so the
   *   subscription errors after 5 seconds if the API has not responded.
   * - On any non-2xx response, network error, timeout, or missing `site_id`,
   *   `siteLinkedIds` is left empty. The selector continues to render the
   *   account-level catalogue without badges or the bulk control, and never blocks
   *   the user from making a manual selection.
   */
  private loadSiteLinkedIds(): void {
    if ( !this.site_id ) {
      // No site context. Degrade silently: no badges, no bulk control.
      return;
    }

    // The endpoint returns `{ data: HazardousSubstance[], hazardous_substance_ids: number[], meta: { total } }`.
    // We use `hazardous_substance_ids` because the spec calls for "the resulting IDs"; this
    // also matches the existing usage in `SiteHazardousSubstanceDataSource`. We fall back to
    // `data[].id` when the field is absent (defensive).
    this.siteLinkedSubscription = this.api
      .makeRequestObservable('get', `v1/sites/${this.site_id}/hazardous-substances`, {}, {
        // The site list endpoint paginates by default. Ask for a generous page so that
        // the auto-suggest set covers every substance currently linked to the site.
        // Substance counts per site are small in practice.
        limit: 1000,
        offset: 0
      })
      .pipe(timeout(5000))
      .subscribe({
        next: (response: any) => {
          const ids: number[] = this.extractSiteLinkedIds(response);
          this.siteLinkedIds = new Set<number>(ids);
        },
        error: () => {
          // Non-2xx response, network error, or 5-second timeout. Degrade silently:
          // leave `siteLinkedIds` empty so the selector renders without badges or the
          // bulk control.
          this.siteLinkedIds = new Set<number>();
        }
      });
  }

  /**
   * Extract the linked-substance IDs from the site endpoint response shape.
   *
   * Preferred field is `hazardous_substance_ids`, which the controller emits at the
   * top level of the response alongside the paginated `data` array. When absent, fall
   * back to extracting `id` from each entry of `data`.
   */
  private extractSiteLinkedIds(response: any): number[] {
    if ( !response ) {
      return [];
    }

    if ( Array.isArray(response.hazardous_substance_ids) ) {
      return response.hazardous_substance_ids
        .map((id: any) => Number(id))
        .filter((id: number) => Number.isFinite(id) && id > 0);
    }

    if ( Array.isArray(response.data) ) {
      return response.data
        .map((entry: any) => entry && Number(entry.id))
        .filter((id: number) => Number.isFinite(id) && id > 0);
    }

    if ( Array.isArray(response) ) {
      return response
        .map((entry: any) => entry && Number(entry.id))
        .filter((id: number) => Number.isFinite(id) && id > 0);
    }

    return [];
  }

  /**
   * Load (or reload) the account-level catalogue and track loading, error, and empty
   * state at the component level.
   *
   * `ApiDataSource.makeRequest` swallows errors internally (resetting the data subject
   * to `[]`), which is indistinguishable from a successful empty result. To drive the
   * inline error banner separately from the empty-state message we use the data source's
   * `loadAccountList()` helper, which forwards the underlying request rejection so the
   * component can react to genuine failures.
   */
  loadAccountList(resetOffset: boolean = true) {
    this.loading = true;
    this.loadFailed = false;

    this.dataSource.loadAccountList(resetOffset)
      .then(() => {
        this.loadFailed = false;
      })
      .catch(() => {
        this.loadFailed = true;
      })
      .finally(() => {
        this.loading = false;
        this.hasLoadedOnce = true;
      });
  }

  /**
   * Submitting the search form re-runs the load so the empty/error UI states are also
   * refreshed. The unfiltered list is shown when `dataSource.search` is empty.
   */
  onSearch() {
    this.loadAccountList(true);
  }

  onClearSelected() {
    this.dataSource.selection.clear();
  }

  /**
   * "Select all linked to this site" bulk control (R9.3).
   *
   * Adds every ID in `siteLinkedIds` to the current selection. This is a strict union:
   * existing manual selections are preserved and no IDs outside `siteLinkedIds` are
   * removed. The button that triggers this method is rendered only when
   * `siteLinkedIds.size > 0`, so this method assumes there is at least one ID to add.
   *
   * The bulk control respects the `multiple` flag implicitly: when `multiple === false`
   * the underlying `SelectionModel` enforces the single-selection invariant by replacing
   * any prior selection with each newly selected ID. In that mode the bulk control is
   * effectively the same as picking the last site-linked ID, but the intended use case
   * is the default `multiple === true` flow.
   */
  onSelectAllLinkedToThisSite(): void {
    this.siteLinkedIds.forEach((id) => {
      this.dataSource.selection.select(id);
    });
  }

  /**
   * Confirm: close the dialog with the selected hazardous substance IDs.
   * Caller wiring uses `if (!results) { return; }` so cancelling preserves prior selection.
   *
   * When the most recent load failed, confirm is a no-op: the calling component should
   * keep its pre-existing selection rather than receive a partial/incorrect result. Cancel
   * remains wired through `onClose()`.
   */
  onSaveAndClose() {
    if ( this.loadFailed ) {
      return;
    }

    let response: number | number[];

    if (this.data['multiple']) {
      response = this.dataSource.selection.selected;
    } else {
      if (this.dataSource.selection.selected.length) {
        response = this.dataSource.selection.selected[0];
      } else {
        response = 0;
      }
    }

    this.dialogRef.close(response);
  }

  onClose() {
    this.dialogRef.close();
  }
}

/**
 * Data source for the SWMS/RAMS Hazardous Substances picker.
 *
 * Loads from `v1/hazardous_substances` (Account_HS_Source). Search maps to the existing
 * `search` query parameter on that endpoint. The non-sortable `linked_to_site` virtual
 * column rendered in the template is populated by the site auto-suggest fetch added in
 * task 7.4. This data source is only responsible for the account list.
 */
export class HazardousSubstancesSelectorForTaDataSource extends ApiDataSource {
  order_by = 'substance';
  order = 'asc';

  /**
   * Standard `getData()` is preserved so the data source remains compatible with any
   * caller that uses the conventional `ApiDataSource` API. The component itself uses
   * `loadAccountList()` so it can detect request failures (which `makeRequest` would
   * otherwise silently turn into an empty list).
   */
  getData(resetOffset: boolean = false) {
    this.makeRequest('v1/hazardous_substances', resetOffset);
  }

  /**
   * Load the account-level catalogue and surface request failures to the caller. This
   * mirrors the request shape used by `ApiDataSource.makeRequest` (search, paging, sort)
   * but does not swallow errors: the returned Promise rejects on any non-2xx response,
   * network error, or timeout so the component can render an inline error banner and
   * suppress confirm.
   */
  loadAccountList(resetOffset: boolean = false): Promise<void> {
    if ( resetOffset ) {
      this.offset = 0;
    }

    return this.api.makeRequest('get', 'v1/hazardous_substances', {}, {
      limit: this.limit,
      offset: this.offset,
      order_by: this.order_by,
      order: this.order,
      search: this.search,
      only_archived: this.only_archived
    }).then((response: any) => {
      const data = response && response.data ? response.data : (response || []);
      const total = response && response.meta && typeof response.meta.total !== 'undefined'
        ? response.meta.total
        : (response && typeof response.total !== 'undefined' ? response.total : 0);

      this.dataSubject.next(data);
      this.totalSubject.next(total);
      this.allIds = (response && response.allIds) ? response.allIds : [];
    }).catch((error: any) => {
      this.dataSubject.next([]);
      this.totalSubject.next(0);
      this.allIds = [];
      throw error;
    });
  }
}
